// src/components/DocumentForm/HealthConditionAllowanceForm.jsx
// 傷病手当金申請書 - 患者入力フォーム
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../../utils/axios';
import { toJstDateInputValue } from '../../utils/dateTimeJst';
import './DocumentForm.scss';

const HealthConditionAllowanceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [formRequest, setFormRequest] = useState(null);
  const [step, setStep] = useState(1);
  // 本日の日付をYYYY-MM-DD形式で取得
  const getTodayString = () => {
    return toJstDateInputValue();
  };

  const [formData, setFormData] = useState({
    full_name: '',
    unable_work_start_date: getTodayString(),
    unable_work_end_date: getTodayString(),
    has_consultation_during_period: null,
    joined_kenpo: true,
    insurance_name: '',
    use_clinic_format: true,
    uploaded_document: null,
    receive_document_by_mail: false,
  });
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [consultationError, setConsultationError] = useState(false);

  // フォームデータ取得
  const fetchFormRequest = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/api/medichat/document_form_requests/${id}`);
      setFormRequest(res.data);
      
      // 既存のフォームデータがあれば復元
      if (res.data.form_data && Object.keys(res.data.form_data).length > 0) {
        setFormData(prev => ({ ...prev, ...res.data.form_data }));
      } else if (res.data.patient) {
        setFormData(prev => ({
          ...prev,
          full_name: res.data.patient.full_name || ''
        }));
      }
      
      // 既に送信済みの場合
      if (res.data.status === 'submitted' || res.data.status === 'approved') {
        setSubmitSuccess(true);
      }
    } catch (err) {
      console.error('[HealthConditionAllowanceForm] fetch error:', err);
      setError('フォーム情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchFormRequest();
  }, [fetchFormRequest]);

  // 入力値変更
  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  // ファイル選択
  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, uploaded_document: file }));
  };

  // 次へ進む
  const nextStep = () => {
    // Step 5で受診していない場合はエラー
    if (step === 5 && formData.has_consultation_during_period === false) {
      setConsultationError(true);
      return;
    }
    setStep(prev => prev + 1);
    window.scrollTo(0, 0);
  };

  // 前へ戻る
  const prevStep = () => {
    setStep(prev => prev - 1);
    window.scrollTo(0, 0);
  };

  // バリデーション
  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.full_name.trim() !== '';
      case 3:
        return formData.unable_work_start_date !== '';
      case 4:
        return formData.unable_work_end_date !== '';
      case 5:
        return formData.has_consultation_during_period !== null;
      case 6:
        return true; // 協会けんぽは選択必須だが初期値あり
      case 7:
        return !formData.joined_kenpo ? formData.insurance_name.trim() !== '' : true;
      default:
        return true;
    }
  };

  // フォーム送信
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'uploaded_document' && formData[key]) {
          submitData.append('uploaded_document', formData[key]);
        } else if (formData[key] !== null) {
          submitData.append(key, formData[key]);
        }
      });

      await axiosInstance.post(`/api/medichat/document_form_requests/${id}/submit`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSubmitSuccess(true);
    } catch (err) {
      console.error('[HealthConditionAllowanceForm] submit error:', err);
      setError(err.response?.data?.error || '送信に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  // 総ステップ数の計算（条件分岐あり）
  const getTotalSteps = () => {
    let total = 10;
    if (formData.joined_kenpo) total -= 1; // 協会けんぽの場合は保険者名称スキップ
    if (formData.use_clinic_format) total -= 1; // 当院書式の場合はファイルアップロードスキップ
    return total;
  };

  // ローディング
  if (loading) {
    return (
      <div className="mc-docform-loading">
        <div className="loader" />
        <p>読み込み中...</p>
      </div>
    );
  }

  // エラー
  if (error && !formRequest) {
    return (
      <div className="mc-docform-error">
        <h2>エラー</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/medichat/mypage')}>マイページへ戻る</button>
      </div>
    );
  }

  // 送信成功
  if (submitSuccess) {
    return (
      <div className="mc-docform-success">
        <div className="mc-docform-success-content">
          <div className="mc-docform-success-icon">✓</div>
          <h2>提出が完了しました</h2>
          <p>
            当院で内容を確認後、発行が可能でしたらLINEにて傷病手当金申請書のPDFをお送りしますので少々お待ちください。
          </p>
          {!formData.use_clinic_format && !formData.receive_document_by_mail && (
            <div className="mc-docform-success-address">
              <p>原本（紙）を以下の住所までお送りください：</p>
              <div className="mc-docform-address-box">
                <p>〒105-0014</p>
                <p>東京都港区芝3丁目15-13 YODAビル2F</p>
                <p>CureNowカスタマーサポート宛</p>
              </div>
            </div>
          )}
        </div>
        <div className="mc-docform-success-footer">
          <button 
            className="mc-docform-btn-primary"
            onClick={() => navigate('/medichat/mypage')}
            style={{ width: '100%' }}
          >
            マイページへ戻る
          </button>
        </div>
      </div>
    );
  }

  // 受診エラー
  if (consultationError) {
    return (
      <div className="mc-docform-error">
        <h2>傷病手当金申請書を発行できません</h2>
        <p>労務不能期間中に当院の受診がなければ発行することはできません。</p>
        <p>労務不能期間をご確認ください。</p>
        <button 
          className="mc-docform-btn-secondary"
          onClick={() => {
            setConsultationError(false);
            setStep(3);
          }}
        >
          労務不能期間を修正する
        </button>
        <button 
          className="mc-docform-btn-primary"
          onClick={() => navigate('/medichat/mypage')}
        >
          マイページへ戻る
        </button>
      </div>
    );
  }

  return (
    <div className="mc-docform-container">
      <header className="mc-docform-header">
        <h1>傷病手当金申請書</h1>
        <div className="mc-docform-progress">
          ステップ {step} / {getTotalSteps()}
        </div>
      </header>

      <main className="mc-docform-main">
        {error && <div className="mc-docform-error-msg">{error}</div>}

        {/* Step 1: 氏名 */}
        {step === 1 && (
          <div className="mc-docform-step">
            <h2 className="mc-docform-question">Q1: 氏名をフルネームでご記入ください</h2>
            <input
              type="text"
              className="mc-docform-input"
              value={formData.full_name}
              onChange={(e) => handleChange('full_name', e.target.value)}
              placeholder="山田 太郎"
            />
          </div>
        )}

        {/* Step 2: 説明 */}
        {step === 2 && (
          <div className="mc-docform-step">
            <h2 className="mc-docform-question">労働不能期間についての解説</h2>
            <div className="mc-docform-info-box">
              <h3>【労働不能期間の考え方】</h3>
              <ul>
                <li>当院での初診日よりも過去の休職期間については、遡って申請することはできません。</li>
                <li>当院での発行日よりも未来の休職期間については、予め申請することはできません。</li>
              </ul>
            </div>
            <div className="mc-docform-info-box">
              <h3>【申請の可否】</h3>
              <p>
                傷病手当金を申請するためには、少なくとも<span className="mc-docform-red">2週間に1回</span>の診察が必要です。
                申請書の主治医記載欄には診療日を記載する必要があるため、全く診察がない月には申請することができません。
              </p>
            </div>
          </div>
        )}

        {/* Step 3: 開始日 */}
        {step === 3 && (
          <div className="mc-docform-step">
            <h2 className="mc-docform-question">Q2: 労務不能期間の開始日を教えてください</h2>
            <p className="mc-docform-hint">（実際に休職を開始した日付を入力してください）</p>
            <div className="mc-docform-notes">
              <p>※当院での初診日から過去の日付での申請はできません。</p>
              <p>※過去に申請した期間については申請することができません。</p>
              <p>※他院通院中の休職期間については、当院で証明することができません。</p>
            </div>
            <input
              type="date"
              className="mc-docform-input mc-docform-date"
              value={formData.unable_work_start_date}
              onChange={(e) => handleChange('unable_work_start_date', e.target.value)}
            />
          </div>
        )}

        {/* Step 4: 終了日 */}
        {step === 4 && (
          <div className="mc-docform-step">
            <h2 className="mc-docform-question">Q3: 労務不能期間の終了日を教えてください</h2>
            <div className="mc-docform-info-box">
              <h3>【現在も休職中の方】</h3>
              <p>前月末日を入力してください</p>
              <p className="mc-docform-small">※会社から指定がある場合は、その日付を入力してください</p>
            </div>
            <div className="mc-docform-info-box">
              <h3>【休職満了している方】</h3>
              <p>実際に休職が終了した日を入力してください</p>
              <p className="mc-docform-small">※当院での最終診察日より未来の日付は入力しないでください。</p>
            </div>
            <input
              type="date"
              className="mc-docform-input mc-docform-date"
              value={formData.unable_work_end_date}
              onChange={(e) => handleChange('unable_work_end_date', e.target.value)}
            />
          </div>
        )}

        {/* Step 5: 受診確認 */}
        {step === 5 && (
          <div className="mc-docform-step">
            <h2 className="mc-docform-question">Q4: 申請する労務不能期間中に当院で受診していますか？</h2>
            <p className="mc-docform-hint">
              ※ひと月でも受診していない場合は、その期間の申請が通りませんので申請書の発行はできません。
            </p>
            <div className="mc-docform-radio-group">
              <label className={`mc-docform-radio ${formData.has_consultation_during_period === true ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="consultation"
                  checked={formData.has_consultation_during_period === true}
                  onChange={() => handleChange('has_consultation_during_period', true)}
                />
                <span>受診している</span>
              </label>
              <label className={`mc-docform-radio ${formData.has_consultation_during_period === false ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="consultation"
                  checked={formData.has_consultation_during_period === false}
                  onChange={() => handleChange('has_consultation_during_period', false)}
                />
                <span>受診していない</span>
              </label>
            </div>
          </div>
        )}

        {/* Step 6: 協会けんぽ */}
        {step === 6 && (
          <div className="mc-docform-step">
            <h2 className="mc-docform-question">Q5: 全国健康保険協会（協会けんぽ）に加入していますか？</h2>
            <img
              src="/images/kyokai-kenpo-example.png"
              alt="協会けんぽの傷病手当金申請書の例"
              className="mc-docform-example-img"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <p className="mc-docform-hint">※保険証に記載の「保険者名称」をご確認ください。</p>
            <p className="mc-docform-red">※間違いが多く発生しています。修正が発生する場合は、追加料金が発生いたします。</p>
            <div className="mc-docform-radio-group">
              <label className={`mc-docform-radio ${formData.joined_kenpo === true ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="kenpo"
                  checked={formData.joined_kenpo === true}
                  onChange={() => handleChange('joined_kenpo', true)}
                />
                <span>加入している</span>
              </label>
              <label className={`mc-docform-radio ${formData.joined_kenpo === false ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="kenpo"
                  checked={formData.joined_kenpo === false}
                  onChange={() => handleChange('joined_kenpo', false)}
                />
                <span>加入していない</span>
              </label>
            </div>
          </div>
        )}

        {/* Step 7: 保険者名称（協会けんぽ以外の場合） */}
        {step === 7 && !formData.joined_kenpo && (
          <div className="mc-docform-step">
            <h2 className="mc-docform-question">Q6: 保険証の保険者名称を入力ください</h2>
            <input
              type="text"
              className="mc-docform-input"
              value={formData.insurance_name}
              onChange={(e) => handleChange('insurance_name', e.target.value)}
              placeholder="〇〇健康保険組合"
            />
          </div>
        )}

        {/* Step 7 or 8: 書式選択 */}
        {((step === 7 && formData.joined_kenpo) || (step === 8 && !formData.joined_kenpo)) && (
          <div className="mc-docform-step">
            <h2 className="mc-docform-question">Q{formData.joined_kenpo ? 6 : 7}: 会社指定の申請書がなければ、当院の書式でデータ発行します。</h2>
            <p className="mc-docform-red">※対面クリニックで指定の書類を提出した場合は「会社指定の書式」を選択</p>
            <div className="mc-docform-radio-group">
              <label className={`mc-docform-radio ${formData.use_clinic_format === true ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="format"
                  checked={formData.use_clinic_format === true}
                  onChange={() => handleChange('use_clinic_format', true)}
                />
                <span>当院の書式</span>
              </label>
              <label className={`mc-docform-radio ${formData.use_clinic_format === false ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="format"
                  checked={formData.use_clinic_format === false}
                  onChange={() => handleChange('use_clinic_format', false)}
                />
                <span>会社指定の書式</span>
              </label>
            </div>
          </div>
        )}

        {/* ファイルアップロード（会社指定書式の場合） */}
        {((step === 8 && formData.joined_kenpo && !formData.use_clinic_format) || 
          (step === 9 && !formData.joined_kenpo && !formData.use_clinic_format)) && (
          <div className="mc-docform-step">
            <h2 className="mc-docform-question">会社指定の書式をご提出ください</h2>
            <p className="mc-docform-hint">※PDF, Word, Excelファイルのみ対応しております。</p>
            <label className="mc-docform-file-input">
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx"
                onChange={handleFileChange}
              />
              {formData.uploaded_document?.name || 'クリックでファイルを選択'}
            </label>
          </div>
        )}

        {/* 郵送希望 */}
        {((step === 8 && formData.joined_kenpo && formData.use_clinic_format) ||
          (step === 9 && formData.joined_kenpo && !formData.use_clinic_format) ||
          (step === 9 && !formData.joined_kenpo && formData.use_clinic_format) ||
          (step === 10 && !formData.joined_kenpo && !formData.use_clinic_format)) && (
          <div className="mc-docform-step">
            <h2 className="mc-docform-question">Q{getTotalSteps() - 1}: 書類の受け取りは郵送を希望しますか？</h2>
            <p className="mc-docform-red">※別途1,100円（税込）かかります。</p>
            <div className="mc-docform-radio-group">
              <label className={`mc-docform-radio ${formData.receive_document_by_mail === false ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="mail"
                  checked={formData.receive_document_by_mail === false}
                  onChange={() => handleChange('receive_document_by_mail', false)}
                />
                <span>希望しない（PDFで受け取り）</span>
              </label>
              <label className={`mc-docform-radio ${formData.receive_document_by_mail === true ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="mail"
                  checked={formData.receive_document_by_mail === true}
                  onChange={() => handleChange('receive_document_by_mail', true)}
                />
                <span>希望する（+1,100円）</span>
              </label>
            </div>
          </div>
        )}
      </main>

      <footer className="mc-docform-footer">
        <button
          className="mc-docform-btn-secondary"
          onClick={prevStep}
          disabled={step === 1}
        >
          戻る
        </button>

        {/* 最終ステップかどうかの判定 */}
        {((step === 8 && formData.joined_kenpo && formData.use_clinic_format) ||
          (step === 9 && formData.joined_kenpo && !formData.use_clinic_format) ||
          (step === 9 && !formData.joined_kenpo && formData.use_clinic_format) ||
          (step === 10 && !formData.joined_kenpo && !formData.use_clinic_format)) ? (
          <button
            className="mc-docform-btn-primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? '送信中...' : '送信する'}
          </button>
        ) : (
          <button
            className="mc-docform-btn-primary"
            onClick={nextStep}
            disabled={!canProceed()}
          >
            次へ進む
          </button>
        )}
      </footer>
    </div>
  );
};

export default HealthConditionAllowanceForm;

